import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FaBookmark, FaTimes, FaExpand, FaCompress, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const BookReader = ({ bookUrl, courseId, isPurchased = false, onPageChange }) => {
  const [rawPages, setRawPages] = useState([]);
  const [sharedStyles, setSharedStyles] = useState('');
  const [sharedScripts, setSharedScripts] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPreviewMessage, setShowPreviewMessage] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [bookColor, setBookColor] = useState('#3b82f6');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showPageJump, setShowPageJump] = useState(false);
  const [pageInput, setPageInput] = useState('');
  const [showZoomInput, setShowZoomInput] = useState(false);
  const [zoomInput, setZoomInput] = useState('');
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  const MAX_PREVIEW_PAGES = 3;

  useEffect(() => {
    loadBook();
  }, [bookUrl]);

  // Load saved page position on mount
  useEffect(() => {
    if (courseId && rawPages.length > 0) {
      const savedPage = localStorage.getItem(`bookReader_${courseId}_page`);
      if (savedPage) {
        const pageNum = parseInt(savedPage, 10);
        if (pageNum >= 0 && pageNum < rawPages.length) {
          setCurrentPage(pageNum);
        }
      }
    }
  }, [courseId, rawPages.length]);

  // Save current page to localStorage whenever it changes
  useEffect(() => {
    if (courseId && rawPages.length > 0) {
      localStorage.setItem(`bookReader_${courseId}_page`, currentPage.toString());
    }
  }, [currentPage, courseId, rawPages.length]);

  useEffect(() => {
    if (onPageChange) {
      onPageChange(currentPage + 1);
    }
  }, [currentPage, onPageChange]);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.25));
  };

  const handleZoomReset = () => {
    setZoomLevel(1);
  };

  const handleZoomSet = (val) => {
    const num = parseInt(val, 10);
    if (num >= 25 && num <= 300) {
      setZoomLevel(num / 100);
    }
    setShowZoomInput(false);
    setZoomInput('');
  };

  // Apply zoom inside iframe via CSS zoom property - no transform/scale needed
  useEffect(() => {
    if (iframeRef.current) {
      try {
        const doc = iframeRef.current.contentWindow.document;
        doc.documentElement.style.setProperty('--zoom-level', zoomLevel);
        doc.body.style.zoom = zoomLevel;
      } catch (err) {}
    }
  }, [zoomLevel]);

  const handlePageJump = (pageNum) => {    const target = parseInt(pageNum, 10) - 1;
    if (target >= 0 && target < pages.length) {
      if (!isPurchased && target >= MAX_PREVIEW_PAGES) {
        setShowPreviewMessage(true);
        return;
      }
      setCurrentPage(target);
      setShowPageJump(false);
      setPageInput('');
    }
  };

  const loadBook = async () => {
    try {
      setLoading(true);
      const response = await fetch(bookUrl);
      if (!response.ok) throw new Error('فشل تحميل الكتاب');
      
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const styles = doc.querySelectorAll('style, link[rel="stylesheet"]');
      const scripts = doc.querySelectorAll('script');
      const styleHTML = Array.from(styles).map(style => style.outerHTML).join('\n');
      const scriptHTML = Array.from(scripts).map(script => script.outerHTML).join('\n');
      
      doc.querySelectorAll('.view-toggle, .logo').forEach(el => el.remove());

      const origin = window.location.origin;
      doc.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src') || '';
        if (src.startsWith('LOGO/')) {
          img.setAttribute('src', `${origin}/${src.replace('LOGO/', '')}`);
        }
      });
      
      const pageElements = doc.querySelectorAll('.page');
      
      if (pageElements.length === 0) {
        setRawPages([html]);
        setLoading(false);
        return;
      }
      
      const extracted = Array.from(pageElements).map(page => {
        const pageClone = page.cloneNode(true);
        pageClone.querySelectorAll('footer, header, nav, .footer, .header, .navigation').forEach(el => el.remove());
        return pageClone.outerHTML;
      });
      
      const colorMatch = styleHTML.match(/\.subtitle\s*\{[^}]*color:\s*(#[0-9a-fA-F]{6})/);
      if (colorMatch) {
        setBookColor(colorMatch[1]);
      } else {
        const fallback = styleHTML.match(/\.cover-decoration\s*\{[^}]*?#([0-9a-fA-F]{6})/);
        if (fallback) setBookColor('#' + fallback[1]);
      }

      setSharedStyles(styleHTML);
      setSharedScripts(scriptHTML);
      setRawPages(extracted);
      setLoading(false);
    } catch (err) {
      console.error('Book load error:', err);
      setError('خطأ في تحميل الكتاب');
      setLoading(false);
    }
  };

  const pages = useMemo(() => {
    if (!sharedStyles && rawPages.length <= 1) return rawPages;

    return rawPages.map(pageHTML => `
      <!DOCTYPE html>
      <html lang="ar" dir="rtl">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes">
        ${sharedStyles}
        <style>
          .view-toggle, .logo { display: none !important; }
          * { position: relative !important; }
          body { zoom: var(--zoom-level, 1); }
          
          /* Mobile A4 fit styles - auto height based on content */
          @media (max-width: 768px) {
            html, body {
              width: 100vw !important;
              height: auto !important;
              overflow-x: hidden !important;
              overflow-y: visible !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .page {
              width: 100vw !important;
              min-width: 100vw !important;
              max-width: 100vw !important;
              height: auto !important;
              min-height: 100vh !important;
              padding: 2vh 3vw !important;
              margin: 0 !important;
              box-shadow: none !important;
              overflow-x: hidden !important;
              overflow-y: visible !important;
              box-sizing: border-box !important;
            }
            .page > * {
              max-width: 100% !important;
              box-sizing: border-box !important;
            }
            .page * {
              box-sizing: border-box !important;
            }
            
            /* Set explicit font sizes for all elements */
            .page h1, .page h1 * { 
              font-size: 4vw !important;
              line-height: 1.4 !important; 
              margin: 1.5vh 0 !important; 
              font-weight: bold !important;
            }
            .page h2, .page h2 * { 
              font-size: 3.5vw !important;
              line-height: 1.4 !important; 
              margin: 1.2vh 0 !important; 
              font-weight: bold !important;
            }
            .page h3, .page h3 * { 
              font-size: 3.2vw !important;
              line-height: 1.4 !important; 
              margin: 1vh 0 !important; 
              font-weight: bold !important;
            }
            .page p, .page p *, 
            .page li, .page li *, 
            .page span, 
            .page div { 
              font-size: 3vw !important;
              line-height: 1.6 !important; 
              margin: 1vh 0 !important; 
            }
            
            /* Add more spacing between major sections - but NOT for boxes */
            .page > div:not([style*="border"]),
            .page > section:not([style*="border"]) {
              margin: 2vh 0 !important;
            }
            
            /* Regular images - reasonable size */
            .page img { 
              max-width: 70% !important; 
              max-height: 15vh !important; 
              height: auto !important;
            }
            
            /* Small inline icons - keep them icon-sized */
            .page img[width*="16"],
            .page img[width*="20"],
            .page img[width*="24"],
            .page img[width*="32"],
            .page img[style*="width: 16"],
            .page img[style*="width: 20"],
            .page img[style*="width: 24"],
            .page img[style*="width: 32"],
            .page svg {
              max-width: 1.2em !important;
              max-height: 1.2em !important;
              width: auto !important;
              height: auto !important;
              display: inline !important;
              vertical-align: middle !important;
            }
            
            /* Box spacing is handled via JavaScript onLoad for accuracy */
            
            /* Header section - make it compact */
            .page .header, .page header, .page .page-header {
              padding: 0.5vh 0 !important;
              margin-bottom: 1.5vh !important;
            }
            
            /* Tables - make them much more compact for mobile */
            .page table, .page table * {
              font-size: 2.8vw !important;
            }
            .page table {
              width: 100% !important;
              border-collapse: collapse !important;
              margin: 1.5vh 0 !important;
            }
            .page table th,
            .page table td {
              padding: 0.8vh 1vh !important;
              line-height: 1.4 !important;
              border: 1px solid #ddd !important;
              vertical-align: middle !important;
              font-size: 2.8vw !important;
            }
            .page table th {
              background-color: #f8f9fa !important;
              font-weight: bold !important;
            }
            .page table td {
              font-size: 2.6vw !important;
            }
            .page table p, .page table span, .page table div {
              margin: 0.3vh 0 !important;
              line-height: 1.4 !important;
              font-size: 2.6vw !important;
            }
            
            /* ONLY target Allemni logo - be VERY specific */
            .page header img[src*="allemni"], 
            .page header img[src*="Allemni"],
            .page .header img[src*="allemni"],
            .page .header img[src*="Allemni"],
            .page .page-header img[src*="allemni"],
            .page .page-header img[src*="Allemni"],
            .page > img[src*="allemni"]:first-child,
            .page > img[src*="Allemni"]:first-child {
              max-width: 4vh !important;
              max-height: 4vh !important;
              width: 4vh !important;
              height: auto !important;
              min-width: 0 !important;
              min-height: 0 !important;
            }
            
            /* Cover decoration - slightly bigger only on cover */
            .page.cover-page .cover-decoration img,
            .page.cover-page > img:first-of-type {
              max-width: 20vh !important;
              max-height: 20vh !important;
              width: auto !important;
              height: auto !important;
            }
            
            .page.cover-page {
              height: auto !important;
              padding: 3vh !important;
            }
            /* Cover page - different logo size */
            .page.cover-page img {
              max-height: 22vh !important;
              max-width: 45% !important;
            }
          }
          
          /* Desktop styles */
          @media (min-width: 769px) {
            body { margin: 0; padding: 0; }
            .page {
              max-width: 100% !important;
              min-height: auto !important;
              padding: 30px 40px !important;
              margin: 0 !important;
              box-shadow: none !important;
            }
            .page.cover-page {
              min-height: 85vh !important;
              padding: 40px !important;
            }
          }
          
          .cover-footer {
            position: relative !important;
            bottom: auto !important;
            margin-top: 40px !important;
            text-align: center !important;
          }
          ::-webkit-scrollbar { width: 8px; }
          ::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
          ::-webkit-scrollbar-thumb { background: ${bookColor}; border-radius: 4px; opacity: 0.9; }
          ::-webkit-scrollbar-thumb:hover { background: ${bookColor}; filter: brightness(0.85); }
          * { scrollbar-width: thin; scrollbar-color: ${bookColor} #f1f5f9; }
        </style>
      </head>
      <body class="extended-view" style="margin: 0; padding: 0; background: white;">
        ${pageHTML}
        ${sharedScripts}
      </body>
      </html>
    `);
  }, [rawPages, sharedStyles, sharedScripts, bookColor]);

  const handleNextPage = () => {
    if (!isPurchased && currentPage >= MAX_PREVIEW_PAGES - 1) {
      setShowPreviewMessage(true);
      return;
    }
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const totalPages = pages.length;
  const visiblePages = isPurchased ? totalPages : Math.min(MAX_PREVIEW_PAGES, totalPages);
  const progress = ((currentPage + 1) / totalPages) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text3 dark:text-gray-400">جاري تحميل الكتاب...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" dir="rtl">
      {!isPurchased && (
        <div className="mb-6 p-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaBookmark className="text-2xl" />
              <div>
                <h3 className="font-bold">معاينة مجانية - {MAX_PREVIEW_PAGES} صفحات فقط</h3>
                <p className="text-sm opacity-90">اشتري الكتاب للوصول الكامل لجميع الصفحات</p>
              </div>
            </div>
            <div className="text-2xl font-black">
              {visiblePages}/{totalPages}
            </div>
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        className={`relative overflow-hidden select-none ${
          isFullscreen ? 'bg-gray-900 flex flex-col' : 'rounded-lg shadow-md'
        }`}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 bg-gray-800 text-white" dir="rtl">
          <div className="flex items-center gap-2">
            <img src="/icon.png" alt="Allemni" className="w-5 h-5 sm:w-6 sm:h-6 rounded" />
            <span className="font-bold text-xs sm:text-sm">صفحة {currentPage + 1} من {totalPages}</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Zoom Controls - visible on desktop always, on mobile only in fullscreen */}
            {(
              <div className={`items-center gap-1 border-r border-white/20 pr-3 ${isFullscreen ? 'flex' : 'hidden sm:flex'}`}>
                <button
                  onClick={handleZoomOut}
                  className="text-white/70 hover:text-white transition-colors w-6 h-6 flex items-center justify-center rounded hover:bg-white/10 text-sm font-bold"
                  title="تصغير"
                >
                  -
                </button>
                {showZoomInput ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleZoomSet(zoomInput); }} className="flex items-center">
                    <input
                      type="number"
                      min="25"
                      max="300"
                      value={zoomInput}
                      onChange={(e) => setZoomInput(e.target.value)}
                      placeholder={`${Math.round(zoomLevel * 100)}`}
                      className="w-10 text-center text-xs bg-gray-700 text-white rounded px-0.5 py-0.5 border border-gray-500 focus:border-primary outline-none"
                      autoFocus
                      onBlur={() => { if (!zoomInput) setShowZoomInput(false); }}
                    />
                    <span className="text-white/50 text-xs mr-0.5">%</span>
                  </form>
                ) : (
                  <button
                    onClick={() => { setShowZoomInput(true); setZoomInput(''); }}
                    className="text-white/70 text-xs font-mono min-w-[40px] text-center hover:text-white hover:bg-white/10 rounded px-1 py-0.5 transition-colors"
                    title="اضغط لكتابة نسبة التكبير"
                  >
                    {Math.round(zoomLevel * 100)}%
                  </button>
                )}
                <button
                  onClick={handleZoomIn}
                  className="text-white/70 hover:text-white transition-colors w-6 h-6 flex items-center justify-center rounded hover:bg-white/10 text-sm font-bold"
                  title="تكبير"
                >
                  +
                </button>
                <button
                  onClick={handleZoomReset}
                  className="text-white/70 hover:text-white transition-colors w-6 h-6 flex items-center justify-center rounded hover:bg-white/10 text-xs"
                  title="إعادة ضبط"
                >
                  ⟲
                </button>
              </div>
            )}
            <div className="hidden sm:flex items-center gap-2 text-xs" dir="ltr">
              <div className="w-16 bg-white/20 rounded-full h-1.5">
                <div 
                  className="bg-primary h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <span className="text-white/70">{progress.toFixed(0)}%</span>
            </div>
            <button
              onClick={toggleFullscreen}
              className="text-white/70 hover:text-white transition-colors"
              title={isFullscreen ? 'خروج من الشاشة الكاملة' : 'شاشة كاملة'}
            >
              {isFullscreen ? <FaCompress className="text-sm" /> : <FaExpand className="text-sm" />}
            </button>
          </div>
        </div>

        {/* Book Content */}
        <div 
          className={`relative ${isFullscreen ? 'flex-1 overflow-auto' : 'overflow-auto'}`} 
          style={{ 
            height: isFullscreen ? '100%' : '85vh',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-x pan-y pinch-zoom',
          }}
        >
          <iframe
            ref={iframeRef}
            srcDoc={pages[currentPage] || ''}
            className="border-0"
            style={{
              background: 'white',
              width: '100%',
              height: '100%',
              minHeight: isFullscreen ? '600px' : '100%',
              margin: '0',
              padding: 0,
              touchAction: 'pan-x pan-y pinch-zoom',
            }}
            title={`Page ${currentPage + 1}`}
            sandbox="allow-scripts allow-same-origin"
            scrolling="yes"
            onLoad={(e) => {
              try {
                const doc = e.target.contentWindow.document;
                // Apply current zoom level
                doc.body.style.zoom = zoomLevel;
                const isMobile = window.innerWidth <= 768;
                if (isMobile) {
                  const allEls = doc.querySelectorAll('.page *');
                  allEls.forEach(el => {
                    const cs = window.getComputedStyle(el);
                    const hasBorder = cs.borderStyle !== 'none' && cs.borderWidth !== '0px' && cs.borderColor !== 'rgba(0, 0, 0, 0)';
                    const hasBg = cs.backgroundColor !== 'rgba(0, 0, 0, 0)' && cs.backgroundColor !== 'transparent' && cs.backgroundColor !== 'rgb(255, 255, 255)';
                    if (hasBorder || hasBg) {
                      el.style.setProperty('padding', '8px 12px', 'important');
                      el.style.setProperty('margin-top', '6px', 'important');
                      el.style.setProperty('margin-bottom', '6px', 'important');
                      const children = el.querySelectorAll('p, span, div, h1, h2, h3, h4, h5, h6');
                      children.forEach(child => {
                        child.style.setProperty('margin', '3px 0', 'important');
                        child.style.setProperty('padding', '0', 'important');
                        child.style.setProperty('line-height', '1.4', 'important');
                      });
                    }
                  });
                }
              } catch (err) {
                console.log('Could not adjust box styles:', err);
              }
            }}
          />

          {!isPurchased && currentPage >= MAX_PREVIEW_PAGES - 1 && (
            <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-white dark:from-gray-800 to-transparent flex items-end justify-center pb-8">
              <div className="text-center">
                <div className="text-4xl mb-3">🔒</div>
                <p className="text-lg font-bold text-gray-800 dark:text-white">
                  نهاية المعاينة المجانية
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Bottom navigation */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 px-2 sm:px-4 py-2.5 bg-gray-800" dir="ltr">
          <button
            onClick={() => handlePageJump(1)}
            disabled={currentPage === 0}
            className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              currentPage === 0
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-gray-600 text-white/80 hover:bg-gray-500'
            }`}
            title="الصفحة الأولى"
          >
            «
          </button>
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              currentPage === 0
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
          >
            <span className="flex items-center gap-1"><FaChevronLeft className="text-[10px]" /> السابق</span>
          </button>

          {showPageJump ? (
            <form 
              onSubmit={(e) => { e.preventDefault(); handlePageJump(pageInput); }}
              className="flex items-center gap-1"
            >
              <input
                type="number"
                min="1"
                max={totalPages}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                placeholder={`${currentPage + 1}`}
                className="w-12 text-center text-xs bg-gray-700 text-white rounded px-1 py-1 border border-gray-500 focus:border-primary outline-none"
                autoFocus
                onBlur={() => { if (!pageInput) setShowPageJump(false); }}
              />
              <span className="text-white/50 text-xs">/ {totalPages}</span>
            </form>
          ) : (
            <button
              onClick={() => { setShowPageJump(true); setPageInput(''); }}
              className="text-white/70 text-sm font-mono hover:text-white hover:bg-gray-700 px-2 py-1 rounded transition-colors"
              title="اضغط للانتقال لصفحة محددة"
            >
              {currentPage + 1} / {totalPages}
            </button>
          )}

          <button
            onClick={handleNextPage}
            disabled={currentPage >= pages.length - 1}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              currentPage >= pages.length - 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary-dark'
            }`}
          >
            <span className="flex items-center gap-1">التالي <FaChevronRight className="text-[10px]" /></span>
          </button>
          <button
            onClick={() => handlePageJump(totalPages)}
            disabled={currentPage >= pages.length - 1}
            className={`px-2 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              currentPage >= pages.length - 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-gray-600 text-white/80 hover:bg-gray-500'
            }`}
            title="الصفحة الأخيرة"
          >
            »
          </button>
        </div>
      </div>

      {showPreviewMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fadeIn" onClick={() => setShowPreviewMessage(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 max-w-md w-full animate-slideUp" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowPreviewMessage(false)}
              className="float-left text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FaTimes className="text-2xl" />
            </button>
            
            <div className="text-center">
              <div className="text-6xl mb-4">🔒</div>
              <h2 className="text-2xl font-black text-text dark:text-white mb-4">
                اشتري الكتاب للمتابعة
              </h2>
              <p className="text-text2 dark:text-gray-300 mb-6">
                لقد شاهدت {MAX_PREVIEW_PAGES} صفحات مجانية. اشتري الكتاب الآن للوصول الكامل لجميع الصفحات والمحتوى!
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => window.location.href = `/course/${courseId}`}
                  className="w-full px-6 py-4 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-xl hover:opacity-90 transition-all shadow-lg"
                >
                  اشتري الآن 🛒
                </button>
                <button
                  onClick={() => setShowPreviewMessage(false)}
                  className="w-full px-6 py-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookReader;
